import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./styles/tailwind.css";
import "./styles/index.css";
import "./components/creator/tokens.css";
import "./i18n";
import { isCapacitor } from "./lib/platform";
import { installChunkReloadGuard } from "./lib/chunkReload";

// Recover gracefully when a new deploy invalidates code-split chunk URLs held by
// an already-open session (PWA hit hardest). Must run before any lazy route.
installChunkReloadGuard();

// MOB/SCR — kill the native WebView rubber-band (TestFlight + APK report:
// "page slides a few cm left/right"). Tag <html> so styles/index.css can
// pin html+body to a non-scrollable box and route scroll to #root. Web users
// keep their normal document scroll (apex + Generic admin pages rely on it).
if (isCapacitor()) {
  document.documentElement.classList.add('cap-locked');
}

const container = document.getElementById("root");
const root = createRoot(container);

root.render(<App />);

// PWA service worker — desktop web only, while NoiRock + TCN native apps are
// in TestFlight / APK testing.
// - Capacitor WebView → SW is dead weight inside the native container.
// - Mobile web (UA mobile AND viewport < 1024px, both required) → we don't
//   want users installing a parallel PWA on their phone next to the native app.
// - Desktop web (incl. admin agency consoles) → keep PWA installable.
// Detection is best-effort UA + viewport — modern iPad lies as "Macintosh" and
// will be treated as desktop, which is fine for the admin-on-tablet case.
(function managePwaRegistration() {
  if (!('serviceWorker' in navigator)) return;
  const ua = navigator.userAgent || '';
  const isMobileUA = /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile|BlackBerry|webOS/i.test(ua);
  const isNarrow = window.innerWidth < 1024;
  const disablePwa = isCapacitor() || (isMobileUA && isNarrow);

  if (disablePwa) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    }).catch(() => {});
    return;
  }

  // Dev mode: vite-plugin-pwa serves the SW at a non-stable URL; skip register
  // in dev to avoid confusing cache state. Prod = /sw.js (VitePWA injectManifest).
  if (!import.meta.env.PROD) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
})();
